/**
 * Read adapters for stored `kpi` payloads (plots plan F; contract in
 * `../contract.ts`, stored shapes in docs/deck-design-system/plots/read-adapters.md).
 *
 * Shape only: fields are renamed and copied, never computed, rounded, sorted or
 * crowned. Stored shapes that lack a Python stamp stay on today's readers:
 *
 * - a summary `scorecard` with no `plot` (stored before F): the report's
 *   `resolveMetricFigure` read and its browser sort and crown, kept in
 *   `SummaryScorecard` as the legacy reader;
 * - a summary `kpi_rows` card with no `plot` (stored before F): `KpiListView`'s
 *   `resolveMetricFigure` read and `formatAbsoluteScore`, kept as its legacy
 *   reader;
 * - a deck KPI tile with no `decimals` (stored before the deck producer
 *   stamped them): `KpiTile.formatKpiValue` stays its legacy formatter.
 */

import {
  KpiLabels,
  PlotFigure,
  PlotValueFormat,
} from '@digital-twins/slide-schemas/generated/plot-types'

import { PlotReadAdapter, PlotReadAdapters, StoredPlotPayload } from '../contract'

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

// -- summary card: scorecard with a stamped plot ------------------------------------

/** The stored scorecard payload fields this adapter reads. */
export interface StoredScorecardWithPlot {
  shape: 'scorecard'
  plot: { kind: 'kpi'; data: Record<string, unknown>; props?: Record<string, unknown> }
}

export const kpiScorecardAdapter: PlotReadAdapter<'kpi', StoredScorecardWithPlot> = {
  kind: 'kpi',
  source: { surface: 'summary-card', shape: 'scorecard' },
  isLegacy: false,
  accepts(stored: unknown): stored is StoredScorecardWithPlot {
    if (!isRecord(stored) || stored.shape !== 'scorecard' || !isRecord(stored.plot)) {
      return false
    }
    const { plot } = stored
    return plot.kind === 'kpi' && isRecord(plot.data) && Array.isArray(plot.data.items) && plot.data.items.length > 0
  },
  read(stored: StoredScorecardWithPlot): StoredPlotPayload<'kpi'> {
    // Python validated the plot against the kpi schema before storing it.
    return stored.plot as unknown as StoredPlotPayload<'kpi'>
  },
}

// -- summary card: Insights KPI rows with a stamped plot -----------------------------

/** The stored kpi_rows payload fields this adapter reads. */
export interface StoredKpiRowsWithPlot {
  shape: 'kpi_rows'
  plot: { kind: 'kpi'; data: Record<string, unknown>; props?: Record<string, unknown> }
}

export const kpiRowsAdapter: PlotReadAdapter<'kpi', StoredKpiRowsWithPlot> = {
  kind: 'kpi',
  source: { surface: 'summary-card', shape: 'kpi_rows' },
  isLegacy: false,
  accepts(stored: unknown): stored is StoredKpiRowsWithPlot {
    if (!isRecord(stored) || stored.shape !== 'kpi_rows' || !isRecord(stored.plot)) {
      return false
    }
    const { plot } = stored
    return plot.kind === 'kpi' && isRecord(plot.data) && Array.isArray(plot.data.items) && plot.data.items.length > 0
  },
  read(stored: StoredKpiRowsWithPlot): StoredPlotPayload<'kpi'> {
    // Python validated the plot against the kpi schema before storing it.
    return stored.plot as unknown as StoredPlotPayload<'kpi'>
  },
}

// -- deck slide: a KPI tile that carries its decimals --------------------------------

/** One stored deck KPI tile read (exec-summary `kpis[]`, rating-likert `kpi`). */
export interface StoredKpiTileRead {
  value: number
  valueFormat: 'percent' | 'count' | 'points' | 'currency'
  unit: string
  sentence: string
  decimals: number
  /** Python's low-base flag. Absent on tiles stamped before the flag. */
  lowBase?: boolean
}

const TILE_FORMATS: Record<StoredKpiTileRead['valueFormat'], PlotValueFormat> = {
  percent: 'percent',
  count: 'count',
  points: 'points',
  currency: 'currency',
}

export const kpiDeckTileAdapter: PlotReadAdapter<'kpi', StoredKpiTileRead> = {
  kind: 'kpi',
  source: { surface: 'deck-slide', shape: 'exec-summary.kpis' },
  isLegacy: false,
  accepts(stored: unknown): stored is StoredKpiTileRead {
    return (
      isRecord(stored) &&
      typeof stored.value === 'number' &&
      typeof stored.valueFormat === 'string' &&
      stored.valueFormat in TILE_FORMATS &&
      typeof stored.unit === 'string' &&
      typeof stored.sentence === 'string' &&
      typeof stored.decimals === 'number' &&
      Number.isInteger(stored.decimals)
    )
  },
  read(stored: StoredKpiTileRead): StoredPlotPayload<'kpi'> {
    const figure: PlotFigure = {
      value: stored.value,
      format: TILE_FORMATS[stored.valueFormat],
      decimals: stored.decimals,
      ...(stored.valueFormat === 'currency' ? { currencyCode: stored.unit } : {}),
    }
    const labels: Partial<KpiLabels> = {}
    return {
      kind: 'kpi',
      data: {
        items: [{ id: 'kpi-0', label: stored.sentence, sentence: stored.sentence, figure, lowBase: stored.lowBase === true }],
        labels,
      },
      props: { layout: 'tile', size: 'slide' },
    }
  },
}

export const KPI_READ_ADAPTERS: PlotReadAdapters<'kpi'> = [
  kpiScorecardAdapter as PlotReadAdapter<'kpi', unknown>,
  kpiRowsAdapter as PlotReadAdapter<'kpi', unknown>,
  kpiDeckTileAdapter as PlotReadAdapter<'kpi', unknown>,
]

// -- deck slide: the exec-summary KPI strip as a stamped plot -------------------------

/**
 * The exec-summary slide's stamped `kpi` plot (plots wave 7, D4): one item per
 * `kpis[]` tile, in order. Returns the items when the plot is there and has
 * exactly one item per tile; null otherwise (a slide stored before the stamp
 * keeps `kpiDeckTileAdapter` or `formatKpiValue` per tile). Fields present
 * only, never what a value means.
 */
export function readExecSummaryKpiStrip(plot: unknown, tileCount: number): StoredPlotPayload<'kpi'>['data']['items'] | null {
  if (!isRecord(plot) || plot.kind !== 'kpi' || !isRecord(plot.data) || !Array.isArray(plot.data.items)) {
    return null
  }
  const items = plot.data.items
  if (items.length !== tileCount || !items.every((item) => isRecord(item) && isRecord(item.figure))) {
    return null
  }
  return items as StoredPlotPayload<'kpi'>['data']['items']
}

/** The first adapter that accepts `stored`, read; null when only a legacy reader can draw it. */
export function readStoredKpi(stored: unknown): StoredPlotPayload<'kpi'> | null {
  for (const adapter of KPI_READ_ADAPTERS) {
    if (adapter.accepts(stored)) {
      return adapter.read(stored)
    }
  }
  return null
}
